<template>
  <div class="typeahead">
    <input
      :id="id"
      :name="name"
      :placeholder="placeholder"
      :aria-label="ariaLabel"
      :autocomplete="autocomplete"
      :disabled="disabled"
      :class="inputClass"
      type="text"
      :value="value"
      v-on:input="onInput"
      v-on:focus="onFocus"
      v-on:blur="onBlur"
      v-on:keydown="onKeydown"
    />
    <!--
      The matches are supplied by the caller (the Angular service call stays in the controller).
      This replaces `uib-typeahead`; the `ul > li[role=option] > a` shape is kept because the
      booking specs select options with `getByRole('option')` and `getByText()`.
    -->
    <ul class="dropdown-menu show" role="listbox" v-if="isOpen">
      <li
        v-for="(item, index) in items"
        :key="getKey ? getKey(item) : index"
        :class="{ active: index === activeIndex }"
        role="option"
        v-on:mousedown.prevent="select(item)"
      >
        <slot name="item" :item="item" :query="value">
          <a tabindex="-1" href="#">{{ getLabel(item) }}</a>
        </slot>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'TypeaheadComponent',
  // Declared so the parent's `v-on:input` / `v-on:select` listen for the component's emits, not the
  // native events bubbling from the inner <input> to the root element (Vue 3 falls listeners
  // through; Vue 2 needed `.native`). Without this the caller's handler receives an InputEvent.
  emits: ['input', 'select'],
  props: {
    // The text in the input, owned by the caller. `input` carries every keystroke.
    value: {
      type: String,
      default: '',
    },
    // The current matches, owned by the caller - it does the (Angular) lookup and passes the
    // result down, so this component stays presentational.
    items: {
      type: Array,
      default: () => [],
    },
    // item -> the string shown in the dropdown and used as the option's accessible name.
    getLabel: {
      type: Function,
      required: true,
    },
    // item -> a stable key. Optional; the index is used otherwise.
    getKey: {
      type: Function,
      default: null,
    },
    id: {
      type: String,
      default: undefined,
    },
    name: {
      type: String,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    ariaLabel: {
      type: String,
      default: undefined,
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    inputClass: {
      type: String,
      default: 'form-control',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // The Angular resource field had `ng-keydown` swallowing Enter, so Enter never selected.
    // Kept as a prop for that field; the others select on Enter.
    enterSelects: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      activeIndex: -1,
      focused: false,
    }
  },
  computed: {
    isOpen() {
      return this.focused && this.items.length > 0
    },
  },
  methods: {
    onInput(event) {
      this.activeIndex = -1
      // Every keystroke is emitted. Delaying this would delay the caller's MODEL, not just its
      // lookup: a brand typed and saved within the delay was saved empty (#683). Debounce the
      // request instead - `latestQuery(ms)` does that.
      this.$emit('input', event.target.value)
    },
    onFocus() {
      this.focused = true
    },
    onBlur() {
      this.focused = false
      this.activeIndex = -1
    },
    onKeydown(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (this.items.length) {
          this.activeIndex = Math.min(this.activeIndex + 1, this.items.length - 1)
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.activeIndex = Math.max(this.activeIndex - 1, 0)
      } else if (event.key === 'Enter') {
        if (this.enterSelects && this.activeIndex >= 0 && this.items[this.activeIndex]) {
          event.preventDefault()
          this.select(this.items[this.activeIndex])
        }
      } else if (event.key === 'Tab') {
        // ui-bootstrap's typeahead selects on Tab as well as Enter - the main search box relies
        // on it (tabbing to the first suggestion picks the job).
        if (this.enterSelects && this.items.length) {
          event.preventDefault()
          this.select(this.items[this.activeIndex >= 0 ? this.activeIndex : 0])
        }
      } else if (event.key === 'Escape') {
        this.focused = false
        this.activeIndex = -1
      }
    },
    select(item) {
      this.activeIndex = -1
      this.focused = false
      this.$emit('select', item)
    },
  },
}
</script>

<style scoped>
.typeahead {
  position: relative;
  /* behave like Bootstrap's `.input-group > .form-control` so the input still fills the group
     when the typeahead sits inside one (the job and linked-job fields) */
  flex: 1 1 auto;
  min-width: 0;
}
.typeahead .dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  max-height: 250px;
  overflow-y: auto;
}
</style>
